import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/Loader';
import Paginate from 'components/Paginate';
import TableError from 'components/TableError';
import { DELETE_COMMUNITY_POST, UPDATE_COMMUNITY_POST } from 'containers/community/graphql/community.mutation';
import { COMMUNITY_POSTS_SEARCH } from 'containers/community/graphql/community.query';
import { useRouter } from 'hooks/useRouter';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import confirm from 'reactstrap-confirm';
import { ACTIVE_AND_DELETED_ARRAY, SORT_VALUES_CREATED_DATE } from 'utils/constants';
import {
  buildURL,
  checkIfValueExist,
  createPostDetailsLink,
  printDate,
  printSerialNumbers,
  urlSlugify,
} from 'utils/helper';
import { SMKLinks } from 'utils/links';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { userSelector } from 'common/globalComponents/selectors';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

const stateSelector = createStructuredSelector({
  user: userSelector,
});
const PostsList = () => {
  const { user } = useSelector(stateSelector);

  const router = useRouter();
  const { query } = router;
  const [postsList, setPostsList] = useState([]);
  const [postsPagination, setPostsPagination] = useState({});

  const groupId = query?.id;

  const defaultFilters = {
    active: { value: null, label: 'All' },
    deleted: { value: null, label: 'All' },
    name: '',
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [getPostsRequest, { loading: postsRequestLoading }] = useLazyQuery(COMMUNITY_POSTS_SEARCH, {
    onCompleted: (data) => {
      const payload = data?.admin_community_post_search;
      if (!isEmpty(payload)) {
        setPostsList(payload?.results);
        setPostsPagination(payload?.pagination);
      }
    },
    onError: () => {
      toast.error('Something went wrong!');
    },
  });

  const [markPostActiveInActiveRequest, { loading: markActiveInActiveLoading }] = useMutation(UPDATE_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (!isEmpty(data?.admin_community_post_update)) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Community Post has been Updated `);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const [markPostDeleted, { loading: markDeletedLoading }] = useMutation(DELETE_COMMUNITY_POST, {
    onCompleted: (data) => {
      if (data?.admin_community_post_remove) {
        handleChangePage(parseInt(router?.query?.page, 10) || 1);
        toast.success(`Success! Community Post has been Updated `);
      }
    },
    onError: (error) => {
      toast.error('Something went wrong!');
    },
  });

  const handleChangePage = (page) => {
    getPostsRequest({
      variables: {
        searchDTO: {
          page: page ? page : 1,
          size: 20,
          ...(!isEmpty(groupId) ? { groupId } : {}),
          ...filters,
          active: checkIfValueExist(filters?.active?.value),
          deleted: checkIfValueExist(filters?.deleted?.value),
          name: urlSlugify(filters?.name?.trim()),
          ...SORT_VALUES_CREATED_DATE,
        },
      },
    });
  };

  const handlePostActive = async (data) => {
    // toggle post active
    await markPostActiveInActiveRequest({
      variables: {
        postId: data?.id,
        createDTO: {
          groupId: data?.group?.id,
          active: !data?.active,
          title: data?.title,
        },
      },
    });
  };

  const handlePostDelete = async (data) => {
    const result = await confirm({
      title: `${data?.deleted ? 'Undelete' : 'Delete'} Group`,
      message: `Are you sure, you want to ${data?.deleted ? 'Undelete' : 'Delete'} this Post?`,
      confirmText: 'Confirm',
      confirmColor: 'primary',
      cancelColor: 'link text-danger',
    });
    if (result) {
      if (data?.deleted) {
        await markPostActiveInActiveRequest({
          variables: {
            postId: data?.id,
            createDTO: {
              groupId: data?.group?.id,
              deleted: !data?.deleted,
              active: data?.active,
              title: data?.title,
            },
          },
        });
      } else {
        await markPostDeleted({
          variables: {
            postId: data?.id,
          },
        });
      }
    }
  };

  const applyFilters = () => {
    handleChangePage(1);
  };

  const clearFilters = async () => {
    setFilters({ ...defaultFilters, name: '' });
    await getPostsRequest({
      variables: {
        searchDTO: {
          page: 1,
          size: 20,
          ...(groupId ? { groupId } : {}),
          ...SORT_VALUES_CREATED_DATE,
        },
      },
    });
  };

  useEffect(() => {
    handleChangePage();
  }, []);

  const renderPostsList = () => {
    return (
      <>
        <Card>
          <CardBody>
            <div className="mt-4">
              <div className="row">
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Active</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.active}
                    onChange={(value) => setFilters({ ...filters, active: value })}
                  />
                </div>
                <div className="col-lg-2 mb-4">
                  <label className="form-label">Deleted</label>
                  <Select
                    options={ACTIVE_AND_DELETED_ARRAY}
                    value={filters.deleted}
                    onChange={(value) => setFilters({ ...filters, deleted: value })}
                  />
                </div>

                <div className="col-lg-3 mb-4">
                  <label htmlFor="name" className="form-label">
                    Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Name..."
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="mt-4 pt-2">
              <a className="btn btn-primary me-4" onClick={applyFilters}>
                Apply Filters
              </a>
              <a className="" onClick={clearFilters}>
                Clear
              </a>
            </div>
          </CardBody>
        </Card>

        <Row className="mb-2 mt-4">
          <Col sm="4">
            <div className="search-box me-2 d-inline-block">
              <div className="position-relative font-bold">{postsPagination?.totalCount || 0} Posts Found</div>
            </div>
          </Col>
          {user.entityPermissions?.community_post?.create && (
            <Col sm="8">
              <div className="text-sm-end">
                <Link
                  replace
                  className="btn btn-success btn-rounded waves-effect waves-light mb-2 me-2"
                  to={
                    !isEmpty(groupId)
                      ? buildURL(SMKLinks.COMMUNITY_GROUP_POSTS_ADD, {
                          groupId,
                        })
                      : buildURL(SMKLinks.COMMUNITY_POSTS_ADD)
                  }>
                  <i className="mdi mdi-plus me-1" />
                  Add Post
                </Link>
              </div>
            </Col>
          )}
        </Row>

        <div className="table-responsive" style={{ minHeight: 300 }}>
          <Table className="project-list-table   table-centered table-borderless">
            <thead>
              <tr className="table-warning">
                <th scope="col">S.No.</th>
                <th scope="col">Title</th>
                <th scope="col">Comment Count</th>
                <th scope="col">Reaction Count</th>
                <th scope="col">Created By</th>
                <th scope="col">Created Date</th>
                <th scope="col">isAnonymous</th>
                <th scope="col">isPinned</th>
                <th scope="col">Active</th>
                <th scope="col">Deleted</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              {!isEmpty(postsList) ? (
                postsList?.map((post, index) => {
                  return (
                    <tr key={`post_${post.id}`} className={!post?.active || post?.deleted ? 'bg-danger' : ''}>
                      <td>{printSerialNumbers(postsPagination)[index]}</td>
                      <td>
                        <div className="d-flex">
                          <div>
                            <Link
                              to={buildURL(SMKLinks.COMMUNITY_POST_DETAILS, {
                                id: post?.id,
                              })}>
                              {post?.title}
                            </Link>
                          </div>

                          {post?.id && (
                            <div className="ms-2">
                              <a target="_blank" rel="noreferrer" href={createPostDetailsLink(post)}>
                                <i className="bx bx-link-external fs-4" />
                              </a>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>{post?.stats?.commentCount}</td>
                      <td>{post?.stats?.reactionCount}</td>
                      <td>{post?.createdBy?.displayName}</td>
                      <td>{printDate(post?.createdDate)}</td>
                      <td>
                        <Badge className={post?.isAnonymous ? 'bg-success' : 'bg-danger'}>
                          {post?.isAnonymous ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={post?.isPinned ? 'bg-success' : 'bg-danger'}>
                          {post?.isPinned ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={post?.active ? 'bg-success' : 'bg-danger'}>
                          {post?.active ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>
                        <Badge className={post?.deleted ? 'bg-success' : 'bg-danger'}>
                          {post?.deleted ? 'Yes' : 'No'}
                        </Badge>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle href="#" className="card-drop" tag="i">
                            <i role="button" className="mdi mdi-dots-vertical font-size-18" />
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            <Link
                              to={
                                !isEmpty(groupId)
                                  ? buildURL(SMKLinks.COMMUNITY_GROUP_POSTS_EDIT, {
                                      groupId,
                                      id: post?.id,
                                    })
                                  : buildURL(SMKLinks.COMMUNITY_POSTS_EDIT, {
                                      id: post?.id,
                                    })
                              }>
                              <DropdownItem>
                                <i className="fas fa-pencil-alt text-success m-1 pe-3" />
                                Edit
                              </DropdownItem>
                            </Link>

                            <DropdownItem onClick={() => handlePostActive(post)}>
                              <i
                                className={`fas  ${
                                  post?.active ? 'fa-ban text-danger' : 'fa-power-off text-success'
                                }   m-1 pe-3`}
                              />
                              {post?.active ? 'Disable' : 'Enable'}
                            </DropdownItem>
                            <DropdownItem onClick={() => handlePostDelete(post)}>
                              <i className={'fas fa-trash-alt text-danger m-1 pe-3'} />
                              {post?.deleted ? 'Undelete' : 'Delete'}
                            </DropdownItem>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <TableError />
              )}
            </tbody>
          </Table>

          {!isEmpty(postsList) && <Paginate pageInfo={postsPagination} onChangePage={handleChangePage} />}
        </div>
      </>
    );
  };

  return !isEmpty(groupId) ? (
    <>
      <Loader isActive={postsRequestLoading || markActiveInActiveLoading || markDeletedLoading} />
      {renderPostsList()}
    </>
  ) : (
    <div className="page-content">
      <Loader isActive={postsRequestLoading || markActiveInActiveLoading || markDeletedLoading} />

      <Container fluid>
        <Breadcrumbs title="Community" breadcrumbItem="Community Posts list" />
        {renderPostsList()}
      </Container>
    </div>
  );
};

export default PostsList;
